import { configureStore, ThunkAction, Action } from "@reduxjs/toolkit";
import { combineReducers } from "redux";
import { setupListeners } from "@reduxjs/toolkit/query";
import {
  persistStore,
  persistReducer,
  FLUSH,
  REHYDRATE,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
} from "redux-persist";
import storage from "redux-persist/lib/storage"; // Use browser storage for Next.js

import langReducer from "../../lib/store/features/lang/langSlice";
import alertReducer from "../../lib/store/features/alert/alertSlice";
import backdropReducer from "../../lib/store/features/backdrop/backdropSlice";
import confirmationReducer from "../../lib/store/features/confirmation/confirmationSlice";
import snackbarReducer from "../../lib/store/features/snackbar/snackbarSlice";
import tokenReducer from "../../lib/store/features/token/tokenSlice";
import screenOrderReducer from "../../lib/store/features/screenOrder/screenOrderSlice";
import screenTenderReducer from "../../lib/store/features/screenTender/screenTenderSlice";
import searchReducer from "../../lib/store/features/search/searchSlice";
import userReducer from "../../lib/store/features/user/userSlice";
import notificationReducer from "../../lib/store/features/notifications/notificationSlice";
import orderReducer from "../../lib/store/features/order/orderSlice";
import recordAudioReducer from "../../lib/store/features/recordAudio/recordAudioSlice";

// Persist config
const persistConfig = {
  key: "root",
  storage, // Use default browser storage
  blacklist: [
    "lang",
    "backdrop",
    "alert",
    "confirmation",
    "snackbar",
    "screenOrder",
    "screenChat",
    "screenTender",
    "search",
    "notifications",
    "order",
    "recordAudioReducer",
  ],
};

// Combine reducers
const rootReducer = combineReducers({
  customer: userReducer,
  lang: langReducer,
  alert: alertReducer,
  backdrop: backdropReducer,
  confirmation: confirmationReducer,
  snackbar: snackbarReducer,
  token: tokenReducer,
  screenOrder: screenOrderReducer,
  screenTender: screenTenderReducer,
  search: searchReducer,
  notifications: notificationReducer,
  order: orderReducer,
  recordAudio: recordAudioReducer,
});

// Persist reducer
const persistedReducer = persistReducer(persistConfig, rootReducer);

// Configure the store
export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [
          FLUSH,
          REHYDRATE,
          PAUSE,
          PERSIST,
          PURGE,
          REGISTER,
          "alert/showAlert",
          "alert/hideAlert",
          "confirmation/showModalConfirmation",
          "confirmation/hideModalConfirmation",
        ],
        ignoredPaths: ["alert.handleClose", "confirmation.handleValid"],
      },
    }),
});

// Persistor for the store
export const persistor = persistStore(store);

// Types
export type AppDispatch = typeof store.dispatch;
export type RootState = ReturnType<typeof store.getState>;
export type AppThunk<ReturnType = void> = ThunkAction<
  ReturnType,
  RootState,
  unknown,
  Action<string>
>;

// Enable listeners for RTK Query
setupListeners(store.dispatch);
